/** Same inexpensive SHA-256 proof verified by the guest server; no identity token is exposed. */
export function hasLeadingZeroBits(bytes: Uint8Array, difficulty: number): boolean {
  if (!Number.isInteger(difficulty) || difficulty < 0 || difficulty > bytes.length * 8) return false;
  const whole = Math.floor(difficulty / 8);
  for (let index = 0; index < whole; index += 1) if (bytes[index] !== 0) return false;
  const remainder = difficulty % 8;
  return remainder === 0 || (bytes[whole]! >>> (8 - remainder)) === 0;
}

export async function solveGuestProof(nonce: string, difficulty: number, signal?: AbortSignal): Promise<number> {
  if (!/^[A-Za-z0-9_-]{16,256}$/.test(nonce) || !Number.isInteger(difficulty) || difficulty < 0 || difficulty > 18) {
    throw new Error("Invalid human check. Please try again.");
  }
  const encoder = new TextEncoder();
  const started = performance.now();
  const checkBudget = () => {
    if (signal?.aborted) throw new DOMException("Human check cancelled", "AbortError");
    if (performance.now() - started > 5_000) throw new Error("Human check took too long. Please retry.");
  };
  // Small batches keep touch/input responsive, including on phones. The server chooses difficulty.
  for (let base = 0; base < 4_194_304; base += 32) {
    checkBudget();
    const hashes = await Promise.all(Array.from({ length: 32 }, (_, offset) =>
      crypto.subtle.digest("SHA-256", encoder.encode(`${nonce}:${base + offset}`))));
    // Digest completion can be delayed by browser load or cancellation.
    checkBudget();
    for (let offset = 0; offset < hashes.length; offset += 1) {
      if (hasLeadingZeroBits(new Uint8Array(hashes[offset]!), difficulty)) return base + offset;
    }
    if (base % 256 === 0) await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  throw new Error("Please request a new human check.");
}
